import React, { useContext, useState, useMemo, useImperativeHandle, forwardRef } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "sonner";
import { X } from "lucide-react";

const EditProduct = forwardRef((props, ref) => {
  const { Products, GetProducts, adminId, api } = useContext(GlobalContext);
  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);

  const effectiveAdminId = adminId || sessionAdminId;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [editFiles, setEditFiles] = useState({
    gstFile: null,
    panFile: null,
    iconFile: null,
  });

  useImperativeHandle(ref, () => ({
    openEditModal: (productId) => {
      handleEditClick(productId);
    },
    closeEditModal: () => {
      setIsEditModalOpen(false);
    }
  }));

  const uploadImage = async (file, productName, imageType) => {
    if (!file) return null;

    try {
      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
        });

      const base64Image = await toBase64(file);

      const body = {
        userid: productName,
        item_name: productName.replace(/\s+/g, "_"),
        image_data: base64Image,
        type: imageType,
      };

      const response = await fetch(
        "https://bumppy.in/sales/api/v1/imageUpload.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();

      if (result.status === "0" || result.msg?.includes("Successfully")) {
        return result.image_url;
      } else {
        throw new Error(result.msg || "Upload failed");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      throw error;
    }
  };

  const handleEditFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }
      setEditFiles((prev) => ({
        ...prev,
        [fileType]: file,
      }));
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!effectiveAdminId) {
      toast.error("Admin ID not found!");
      return;
    }

    setUpdateLoading(true);
    try {
      let gstUrl = editingProduct.gstUrl;
      let panUrl = editingProduct.panUrl;
      let iconUrl = editingProduct.iconUrl;

      if (editFiles.gstFile) {
        toast.info("Uploading GST image...");
        gstUrl = await uploadImage(
          editFiles.gstFile,
          editingProduct.productName,
          "gst"
        );
      }
      if (editFiles.panFile) {
        toast.info("Uploading PAN image...");
        panUrl = await uploadImage(
          editFiles.panFile,
          editingProduct.productName,
          "pan"
        );
      }
      if (editFiles.iconFile) {
        toast.info("Uploading Icon image...");
        iconUrl = await uploadImage(
          editFiles.iconFile,
          editingProduct.productName,
          "icon"
        );
      }

      const payload = {
        adminId: String(effectiveAdminId),
        productId: String(editingProduct.productId),
        firmName: editingProduct.firmName,
        productName: editingProduct.productName,
        gst: gstUrl,
        pan: panUrl,
        website: editingProduct.website,
        onboardingURL: editingProduct.onboardingURL,
        iconURL: iconUrl,
      };

      const res = await api.post(`/Admin/v1.0/updateProduct`, payload);

      if (res?.data?.responseCode === "00") {
        toast.success("Product updated successfully!");

        if (typeof GetProducts === "function") {
          await GetProducts();
        }

        setIsEditModalOpen(false);
        setEditingProduct(null);
        setEditFiles({
          gstFile: null,
          panFile: null,
          iconFile: null,
        });
      } else {
        toast.error(res?.data?.responseMessage || "Failed to update product");
      }
    } catch (error) {
      console.error("Update product error:", error);
      toast.error(error?.message || "Something went wrong!");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleEditClick = (productId) => {
    const product = (Products || []).find(
      (p) => String(p.pk_id) === String(productId)
    );
    if (!product) {
      toast.error("Product not found!");
      return;
    }

    setEditingProduct({
      productId: product.pk_id,
      firmName: product.firm_name,
      productName: product.product_name,
      gstUrl: product.gst_photo || "",
      panUrl: product.pan_photo || "",
      iconUrl: product.icon_url || "",
      website: product.website_url || "",
      onboardingURL: product.onboarding_url || "",
    });

    setEditFiles({
      gstFile: null,
      panFile: null,
      iconFile: null,
    });

    setIsEditModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-blue-400 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Edit Product</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-white hover:text-gray-200 text-2xl"
              >
                <X />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product ID
                  </label>
                  <input
                    type="text"
                    value={editingProduct?.productId || ""}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="productName"
                    value={editingProduct?.productName || ""}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Firm Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firmName"
                    value={editingProduct?.firmName || ""}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={editingProduct?.website || ""}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Onboarding URL
                  </label>
                  <input
                    type="text"
                    name="onboardingURL"
                    value={editingProduct?.onboardingURL || ""}
                    onChange={handleInputChange}
                    placeholder="https://example.com/onboarding"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleEditFileChange(e, "gstFile")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                  {editingProduct?.gstUrl && !editFiles.gstFile && (
                    <a
                      href={editingProduct.gstUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline mt-1 block"
                    >
                      View current image
                    </a>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PAN Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleEditFileChange(e, "panFile")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                  {editingProduct?.panUrl && !editFiles.panFile && (
                    <a
                      href={editingProduct.panUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline mt-1 block"
                    >
                      View current image
                    </a>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleEditFileChange(e, "iconFile")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                  {editingProduct?.iconUrl && !editFiles.iconFile && (
                    <a
                      href={editingProduct.iconUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline mt-1 block"
                    >
                      View current image
                    </a>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateProduct}
                  disabled={updateLoading}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {updateLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating...
                    </>
                  ) : (
                    "Update Product"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default EditProduct;